import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { AlertTriangle, Eye, MousePointer, Coins, Check, ArrowRight } from 'lucide-react';

const ProblemSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const icons = [AlertTriangle, Eye, MousePointer, Coins];
  
  return (
    <section 
      id="problem" 
      ref={ref}
      className="section-spacing relative bg-white"
      data-testid="problem-section"
    >
      <div className="section-container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase mb-4 block">
            {t.problem.subtitle}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] max-w-3xl mx-auto">
            {t.problem.title}
          </h2>
        </motion.div>
        
        {/* Problem Cards with Solutions - New Impactful Design */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {t.problem.items.map((item, index) => {
            const Icon = icons[index];
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative group"
                data-testid={`problem-card-${index}`}
              >
                {/* Card Container */}
                <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300">
                  {/* Problem Part - Top */}
                  <div className="p-6 pb-5 border-b border-[#E2E8F0]">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0 group-hover:bg-red-100 transition-colors">
                        <Icon className="w-6 h-6 text-red-500" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-bold text-red-500 uppercase tracking-wider">Problème</span>
                        </div>
                        <h3 className="text-lg font-bold text-[#0F172A] mb-2">
                          {item.title}
                        </h3>
                        <p className="text-[#64748B] text-sm leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Arrow Connector */}
                  <div className="flex justify-center -my-2 relative z-10">
                    <ArrowRight className="w-6 h-6 text-[#0F172A] rotate-90" />
                  </div>
                  
                  {/* Solution Part - Bottom (AQERA) */}
                  <div className="p-6 pt-4 bg-white">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0">
                        <Check className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-bold text-[#2563EB] uppercase tracking-wider">Solution AQERA</span>
                        </div>
                        <h4 className="text-lg font-bold text-[#0F172A] mb-1">
                          {item.solutionTitle}
                        </h4>
                        <p className="text-[#475569] text-sm leading-relaxed">
                          {item.solution}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
        
        {/* Visual Separator */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={isInView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="h-px bg-gradient-to-r from-transparent via-[#E2E8F0] to-transparent mt-20"
        />
      </div>
    </section>
  );
};

export default ProblemSection;
